import { TicketCard } from "@jugl-web/domain-resources/tickets/components/TicketCard";
import { useTicketList } from "@jugl-web/domain-resources/tickets/hooks/useTicketList";
import { ClientContact } from "@jugl-web/rest-api";
import { TicketInboxFilter } from "@jugl-web/rest-api/tickets/models/common-types/TicketInboxFilter";
import {
  DataLoadingWrapper,
  ListBox,
  PlainButton,
  Popover,
} from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { selectUserId } from "@web-src/features/auth/authSlice";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { TicketCreationBlockedTooltip } from "@web-src/modules/client-support/components/TicketCreationBlockedTooltip";
import { useTicketsLimit } from "@web-src/modules/client-support/hooks/useTicketsLimit";
import { useSelector } from "react-redux";
import { collapsedIconButtonClassName } from "../../../../consts";
import { useApplyIdentifiedContactToConversation } from "../../../../hooks/useApplyIdentifiedContactToConversation";
import { useSubscribeToTicketsMutations } from "../../../../hooks/useSubscribeToTicketsMutations";
import { useTicketsFilterOptions } from "../../../../hooks/useTicketsFilterOptions";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useClientSupportPageWithSelectedConversation } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageWithSelectedConversation";
import { IdentifyContactDialog } from "../../../IdentifyContactDialog";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as ChevronDownIcon } from "./assets/chevron-down.svg";

export const TicketsTabContent: FC = () => {
  const { t } = useTranslations();
  const { openCreateTicketSidebar, openTicketDetailsSidebar } =
    useClientSupportPageContext();
  const { selectedConversation } =
    useClientSupportPageWithSelectedConversation();
  const { entityId } = useEntitySelectedProvider();
  const meId = useSelector(selectUserId);
  const { ref: inViewRef, inView } = useInView();
  const { getFilterOptionById, listBoxOptions } = useTicketsFilterOptions();
  const { isTicketCreationBlocked } = useTicketsLimit();
  const [activeFilter, setActiveFilter] = useState<TicketInboxFilter>("all");
  const [isIdentifyContactDialogOpen, setIsIdentifyContactDialogOpen] =
    useState(false);

  const contact = selectedConversation.contact_info;

  const {
    tickets,
    isLoading: isTicketsLoading,
    isLoadingMore,
    isError: isTicketsError,
    isLoadingMoreError,
    hasMore,
    retry: retryTickets,
    loadMore,
    setTickets,
    setTotalCount,
  } = useTicketList({
    entityId,
    contactId: contact?.id || "",
    filter: activeFilter,
    meId: meId ?? "",
    skip: !contact?.id,
    sort: "desc",
    pageSize: 10,
  });

  useSubscribeToTicketsMutations({
    sort: "desc",
    hasMore,
    filter: activeFilter,
    meId: meId ?? "",
    contactId: contact?.id || undefined,
    setTickets,
    setTotalCount,
  });

  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [hasMore, inView, isLoadingMore, loadMore]);

  const activeFilterOption = getFilterOptionById(activeFilter);

  const handleAddTicket = useCallback(() => {
    if (isTicketCreationBlocked) {
      return;
    }

    if (!contact?.id) {
      setIsIdentifyContactDialogOpen(true);
      return;
    }

    openCreateTicketSidebar({
      contact: {
        id: contact.id,
        name: contact.name || "",
      },
    });
  }, [isTicketCreationBlocked, contact, openCreateTicketSidebar]);

  const applyIdentifiedContactToConversation =
    useApplyIdentifiedContactToConversation();

  const handleContactIdentified = useCallback(
    (identifiedContact: ClientContact) => {
      setIsIdentifyContactDialogOpen(false);
      applyIdentifiedContactToConversation(identifiedContact);
      openCreateTicketSidebar({
        contact: {
          id: identifiedContact.id,
          name: identifiedContact.name ?? "Unknown",
        },
      });
    },
    [applyIdentifiedContactToConversation, openCreateTicketSidebar]
  );

  return (
    <div className="flex min-h-full flex-col px-6 py-10">
      <div className="flex shrink-0 items-center justify-between">
        <Popover
          renderTrigger={({ Trigger, triggerRef, isOpen }) => (
            <Trigger
              ref={triggerRef}
              as={PlainButton}
              className={cx(
                collapsedIconButtonClassName,
                "flex h-8 w-max items-center justify-between gap-2 px-2.5"
              )}
            >
              <span className="flex items-center gap-1.5">
                {activeFilterOption.icon}
                <span className="truncate font-secondary text-sm font-medium text-[#1A2023]">
                  {activeFilterOption.label}
                </span>
              </span>
              <ChevronDownIcon
                className={cx(
                  "shrink-0 transition-transform",
                  isOpen && "rotate-180"
                )}
              />
            </Trigger>
          )}
        >
          {({ onClose }) => (
            <ListBox
              items={listBoxOptions}
              isCompact
              selectionBehavior={{ canToggle: true, mode: "single" }}
              renderLabel={({ value }) => value.label}
              renderStartIcon={({ value }) => value.icon}
              defaultSelectedIds={[activeFilter]}
              onSelect={({ id }) => {
                setActiveFilter(id as TicketInboxFilter);
                onClose();
              }}
            />
          )}
        </Popover>
        {isTicketCreationBlocked ? (
          <TicketCreationBlockedTooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                ref={ref}
                className={cx(
                  collapsedIconButtonClassName,
                  "h-8 w-8 shrink-0 cursor-not-allowed opacity-40"
                )}
                {...props}
              >
                <AddIcon />
              </PlainButton>
            )}
          />
        ) : (
          <PlainButton
            className={cx(collapsedIconButtonClassName, "h-8 w-8 shrink-0")}
            onClick={handleAddTicket}
          >
            <AddIcon />
          </PlainButton>
        )}
      </div>

      <div className="mt-6 flex min-h-0 flex-1 flex-col">
        <DataLoadingWrapper
          className="h-full min-h-0 flex-1"
          isError={isTicketsError}
          isLoading={isTicketsLoading}
          onRetry={retryTickets}
          isLoadingMore={isLoadingMore}
          isLoadingMoreError={isLoadingMoreError}
        >
          <div className="flex flex-col gap-6">
            {tickets.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                onClick={() => openTicketDetailsSidebar(ticket.id)}
              />
            ))}
            {!tickets.length && (
              <>
                <div className="flex h-[100px] w-full items-center justify-center rounded-xl border border-dashed border-grey-300 text-center">
                  <span className="font-secondary text-sm leading-[160%] text-dark-600">
                    {t({
                      id: "client-support-page.no-active-tickets-for-customer",
                      defaultMessage:
                        "No active tickets for this Customer yet ✅",
                    })}
                  </span>
                </div>
                <div className="h-[88px] w-full rounded-xl border border-dashed border-grey-300" />
                <div className="h-[50px] w-full rounded-xl border border-dashed border-grey-300" />
              </>
            )}
          </div>
          {hasMore && !isTicketsLoading && !isLoadingMore && (
            <div ref={inViewRef} />
          )}
        </DataLoadingWrapper>
      </div>
      <IdentifyContactDialog
        isOpen={isIdentifyContactDialogOpen}
        onRequestClose={() => setIsIdentifyContactDialogOpen(false)}
        onContactIdentified={handleContactIdentified}
      />
    </div>
  );
};
